import React, { useState, useEffect, useCallback } from "react";
import AddPharmacyItem from "./AddPharmacyItems";
import { getPharmacyItems } from "../../apis/pharmacyItems";
import { getPharmacyItemById, updatePharmacyItem } from "../../apis/pharmacyItems";
import { Pagination, PaginationProps, Tag, Button, Dropdown, Tooltip } from "antd";
import { MedicineBoxOutlined, ExperimentOutlined } from "@ant-design/icons";
import { BeakerIcon, ArchiveBoxIcon } from "@heroicons/react/24/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import CommonPagination from "../../components/CommanPagination";
const getStatusColor = (isActive: boolean) => {
  return isActive ? "green" : "red";
};

const PharmacyItemsList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [editableItem, setEditableItem] = useState<any>(null);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    itemName: "",
    form: "",
    status: "",
  });
  const closeModal = () => setIsModalOpen(false);

  const openModal = () => {
    setEditableItem(null);
    setIsModalOpen(true);
  };

  const fetchItems = useCallback((params: any = { page: 1, per_page: 10 }) => {
    setLoading(true);
    getPharmacyItems(params)
      .then((res: any) => {
        setPharmacyItems(res.data || []);
        setMeta(res.meta);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const fetchWithFilters = useCallback(
    (params: any = {}) => {
      const baseParams: any = {
        page: params.page ?? 1,
        per_page: params.per_page ?? pageSize,
      };
      if (filters.itemName.trim()) baseParams.name = filters.itemName.trim();
      if (filters.form.trim()) baseParams.form = filters.form.trim();
      if (filters.status) {
        if (filters.status === "active") baseParams.is_active = true;
        else if (filters.status === "inactive") baseParams.is_active = false;
      }
      fetchItems({ ...baseParams, ...params });
    },
    [filters, fetchItems, pageSize]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWithFilters({ page: 1, per_page: pageSize });
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.itemName, filters.form, filters.status, fetchWithFilters]);

  const handleEditItem = (item: any) => {
    const itemId = item.id;
    getPharmacyItemById(itemId)
      .then((res: any) => {
        setEditableItem(res);
        setIsModalOpen(true);
      })
      .catch((err) => console.error(err));
  };

  const handleToggleConsumable = (item: any) => {
    const updatedItem = { ...item, isConsumable: !item.isConsumable };
    updatePharmacyItem(item.id, { is_consumable: updatedItem.isConsumable })
      .then(() => {
        setPharmacyItems((prev) =>
          prev.map((i) => (i.id === item.id ? updatedItem : i))
        );
      })
      .catch((err) => console.error(err));
  };

  const handleToggleActive = (item: any) => {
    const updatedItem = { ...item, isActive: !item.isActive };
    updatePharmacyItem(item.id, { is_active: updatedItem.isActive })
      .then(() => {
        setPharmacyItems((prev) =>
          prev.map((i) => (i.id === item.id ? updatedItem : i))
        );
      })
      .catch((err) => console.error(err));
  };

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    setPage(page);
    setPageSize(pageSize ?? 10);
    fetchWithFilters({ page, per_page: pageSize ?? 10 });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Pharmacy Items List</h1>
          <p className="mt-2 text-sm text-gray-700">List of all pharmacy items.</p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={openModal}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Add Pharmacy Item
          </button>
          <button
            type="button"
            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
          >
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flow-root mt-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Item Name</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Form</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Strength</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Unit</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Action</th>
                  </tr>
                  <tr className="border-t border-gray-200">
                    <th className="py-2 pl-4 pr-3 sm:pl-6">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Filter by item name..."
                          value={filters.itemName}
                          onChange={(e) => handleFilterChange("itemName", e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm pr-8 sm:text-sm focus:border-primary focus:ring-primary"
                        />
                        {filters.itemName && (
                          <button
                            type="button"
                            onClick={() => handleFilterChange("itemName", "")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </th>
                    <th className="px-3 py-2">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Filter by form..."
                          value={filters.form}
                          onChange={(e) => handleFilterChange("form", e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm pr-8 sm:text-sm focus:border-primary focus:ring-primary"
                        />
                        {filters.form && (
                          <button
                            type="button"
                            onClick={() => handleFilterChange("form", "")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </th>
                    <th className="px-3 py-2" />
                    <th className="px-3 py-2" />
                    <th className="px-3 py-2">
                      <div className="relative">
                        <select
                          value={filters.status}
                          onChange={(e) => handleFilterChange("status", e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm pr-8 sm:text-sm focus:border-primary focus:ring-primary"
                        >
                          <option value="">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        {filters.status && (
                          <button
                            type="button"
                            onClick={() => handleFilterChange("status", "")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-sm text-gray-500">
                        Loading…
                      </td>
                    </tr>
                  ) : pharmacyItems.length > 0 ? (
                  pharmacyItems.map((item) => (
                    <tr key={item.id}>
                      <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 flex gap-3">
                        <Tooltip title={item.isConsumable ? "Consumable" : "Non-Consumable"}>
                          {item.isConsumable ? (
                            <BeakerIcon
                              className="h-6 w-5 text-black cursor-pointer"
                              onClick={() => handleToggleConsumable(item)}
                            />
                          ) : (
                            <MedicineBoxOutlined
                              style={{ fontSize: "18px", color: "black", cursor: "pointer" }}
                              onClick={() => handleToggleConsumable(item)}
                            />
                          )}
                        </Tooltip>

                        {item.name}
                      </td>
                      <td>{item.form}</td>
                      <td>{item.strength}</td>
                      <td>{item.unit}</td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap">
                        {/* <Tag color={getStatusColor(item.isActive)}>
                          {item.isActive ? "ACTIVE" : "INACTIVE"}
                        </Tag> */}
                        <Tag
                        color={getStatusColor(item.isActive)}
                        onClick={() => handleToggleActive(item)}
                        style={{ cursor: "pointer" }}
                      >
                        {item.isActive ? "ACTIVE" : "INACTIVE"}
                      </Tag>
                      </td>
                      <td className="py-4 px-3 text-sm text-gray-700">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="block px-3 py-2 text-white bg-primary rounded hover:bg-hover"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-sm text-gray-500">
                        No pharmacy items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* <div className="flex justify-end pt-5">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={meta?.total || 0}
                onChange={onPaginationChange}
              />
            </div> */}
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[96vh] overflow-y-auto relative">

            {/* HEADER */}
            <div className="flex items-center justify-between bg-primary text-white px-4 py-3 rounded-t-lg">
              <h2 className="text-lg font-semibold">
                {editableItem ? "Update Pharmacy Item" : "Add Pharmacy Item"}
              </h2>

              <button
                onClick={closeModal}
                className="text-white hover:text-gray-200 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="p-6">
              <AddPharmacyItem
                closeModal={() => {
                  setIsModalOpen(false);
                  fetchWithFilters({ page, per_page: pageSize });
                  setEditableItem(null);
                }}
                editableItem={editableItem}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyItemsList;
